import { FileTemporalityUpload } from '../../Components/FileDropzone/FileFolder';
import XhrRequestHandler, { XhrRequestError } from '../../lib/XhrRequestHandler';
import FileHasher from './FileHasher';
import { FileToUpload } from './FileToUpload';

export const uploadTypeProject = 'proyecto';
export const uploadTypeService = 'servicio';
export const uploadTypeActivity = 'actividad';
export const uploadTypeSettlement = 'liquidacionIndirecta';
export const uploadItemProjectType = 'proyecto';
export const uploadItemServiceType = 'servicio';
export const uploadItemActivityType = 'actividad';
export const uploadItemSettlmentType = 'liquidacionIndirecta';

export interface IUploadDataItem {
  cmdi_id: string;
  cmdi_cmdid: string;
  cmdi_aws_bucket: string;
  cmdi_aws_key: string;
  cmdi_doc_uuid: string;
  cmdi_doctid: string;
  cmdi_pcaid: string;
}

export interface IUploadData {
  cargaDocumentosItems: IUploadDataItem[];
}

interface IUploadDataBaseNew {
  cmd_cant_archivos: number;
  cmd_cant_bytes_total: number;
  cmd_tipo: typeof uploadTypeProject | typeof uploadTypeService | typeof uploadTypeActivity | typeof uploadTypeSettlement;
}

interface IUploadDataItemBaseNew {
  cmdi_cant_bytes: number;
  cmdi_nombre_archivo: string;
  cmdi_doc_uuid: string;
  cmdi_content_type: string;
  doctId: string;
}

export interface IUploadProjectDataItemNew extends IUploadDataItemBaseNew {
  tipo: typeof uploadItemProjectType | typeof uploadItemServiceType;
  doc_periodo?: string;
  doc_ejercicio?: string;
}

interface IUploadActivityDataItemNew extends IUploadDataItemBaseNew {
  actId: string;
  tipo: typeof uploadItemActivityType;
}

interface IUploadSettlementDataItemNew extends IUploadDataItemBaseNew {
  cmdi_dxliid: string;
  tipo: typeof uploadItemSettlmentType;
}

export interface IUploadProjectDataNew extends IUploadDataBaseNew {
  cmd_proid: string;
  folderId?: string;
  cargaDocumentosItems: IUploadProjectDataItemNew[];
}

export interface IUploadSettlementDataNew extends IUploadDataBaseNew {
  cmd_liqid: string;
  cargaDocumentosItems: IUploadSettlementDataItemNew[];
}

export interface IUploadActivityDataNew extends IUploadDataBaseNew {
  cmd_actid: string;
  cargaDocumentosItems: IUploadActivityDataItemNew[];
}

export type IUploadDataNew = IUploadProjectDataNew | IUploadActivityDataNew | IUploadSettlementDataNew;

export type FileUploadTracker = (file: File, loaded: number, total: number) => unknown;

interface IUploadFilesData {
  files: FileToUpload[];
  uploadData: IUploadDataNew;
  callback?: (data: IUploadData) => void
}

export interface IFilesUploadTracker {
  onPreparingUpload: () => void;
  onEndPreparingUpload: () => void;
  onStartUpload: (files: File[], abortController: AbortController) => void;
  /**
   * @TODO ver de separar en casos de error o no
   */
  onEndUpload: () => void;
  onFileProgress: (file: File, loaded: number, total: number) => void;
  onFileUploadError: (file: File) => void;
}

class DocumentsUploader {
  private readonly uploadEntityBase = '/carga-documentos';
  private readonly itemUploadEntityBase = '/carga-documentos-items';

  private filesUploadTrackers: IFilesUploadTracker[];
  private uploadQueue: IUploadFilesData[];
  private uploading: boolean;

  private constructor(private requestHandler: XhrRequestHandler) {
    this.filesUploadTrackers = [];
    this.uploadQueue = [];
    this.uploading = false;
  }

  static start(requestHandler: XhrRequestHandler) {
    return new this(requestHandler);
  }

  registerFileUploadTracker(tracker: IFilesUploadTracker) {
    this.filesUploadTrackers.push(tracker);
  }

  informPreparingUpload() {
    this.filesUploadTrackers.forEach((tracker) => tracker.onPreparingUpload());
  }

  informEndPreparingUpload() {
    this.filesUploadTrackers.forEach((tracker) => tracker.onEndPreparingUpload());
  }

  upload(files: FileToUpload[], uploadData: IUploadDataNew, callback?: (data: IUploadData) => void) {
    if (files.length === 0) throw new Error('No hay archivos para cargar');
    this.uploadQueue.push({ files, uploadData, callback });
    if (!this.uploading) {
      return this.processNextInQueue();
    }
  }

  private async processNextInQueue(): Promise<void> {
    if (this.uploadQueue.length === 0) return Promise.resolve();
    this.uploading = true;
    const { files, uploadData, callback } = this.uploadQueue.shift()!;

    try {
      const data = await this.process(files, uploadData);
      callback?.(data)
    } catch (e) {
      console.log(e);
    } finally {
      this.uploading = false;
      this.processNextInQueue();
    }
  }

  private async process(files: FileToUpload[], uploadData: IUploadDataNew) {
    const data = await this.generateUploadItems(uploadData);
    const abortController = new AbortController();

    const uploadPromises: Promise<void>[] = [];
    data.cargaDocumentosItems.forEach((item) => {
      const file = this.fileByUploadItem(files, item);
      uploadPromises.push(this.uploadFileFromItem(file, item, abortController));
    });

    this.informStartToTrackers(files, abortController);

    try {
      await Promise.all(uploadPromises);
    } catch (e) {
      console.error('uploadError :>>', e);
    } finally {
      this.informEndToTrackers();
    }

    return data;
  }

  private async uploadFileFromItem(file: FileTemporalityUpload, item: IUploadDataItem, abortController: AbortController) {
    const hash = await FileHasher.for(file);
    const url = await this.uploadUrl(item, hash);
    const config = {
      uploadTracker: (loaded: number, total: number) => {
        this.informProgressToTrackers(file, loaded, total);
      },
      abortController,
      headers: {
        'Content-MD5': hash,
      },
    };

    try {
      // await Waiter.waitFor(5000);
      await this.requestHandler.unauthenticatedPutForUpload(url, file, config);
      console.log({file, item, });
      await this.informSuccessfulUpload(item, file.period);
    } catch (e) {
      if (e instanceof XhrRequestError && e.isCancel()) {
        await this.informCancelUpload(item);
      } else {
        let msg = undefined;
        if (e instanceof XhrRequestError) {
          const requestData = e.responseData();
          console.log('requestData :>> ', requestData);
        }
        await this.informErrorUpload(item, msg);
      }
      this.informFileErrorToTrackers(file);
    }
  }

  private informFileErrorToTrackers(file: File) {
    this.filesUploadTrackers.forEach((tracker) => tracker.onFileUploadError(file));
  }

  private informProgressToTrackers(file: File, loaded: number, total: number) {
    this.filesUploadTrackers.forEach((tracker) => tracker.onFileProgress(file, loaded, total));
  }

  private informEndToTrackers() {
    this.filesUploadTrackers.forEach((tracker) => tracker.onEndUpload());
  }

  private informStartToTrackers(filesToUpload: FileToUpload[], abortController: AbortController) {
    const newLocal = filesToUpload.map((f) => f.getFile());
    this.filesUploadTrackers.forEach((tracker) => tracker.onStartUpload(newLocal, abortController));
  }

  private async uploadUrl({ cmdi_id }: IUploadDataItem, fileHash: string) {
    const response = await this.requestHandler.get<{ url: string }>(
        `${this.itemUploadEntityBase}/temporal-upload-url/${cmdi_id}?hash=${encodeURIComponent(fileHash)}`
    );

    return response.url;
  }

  private informSuccessfulUpload({ cmdi_id }: IUploadDataItem, referDate?: string) {
    return this.requestHandler.post(`${this.itemUploadEntityBase}/inform-upload/${cmdi_id}${referDate? `?referDate=${referDate}` : ''}`);
  }

  private informErrorUpload({ cmdi_id }: IUploadDataItem, msg?: string) {
    const data = msg ? { msg } : undefined;
    return this.requestHandler.post<unknown, { msg?: string }>(
        `${this.itemUploadEntityBase}/inform-error/${cmdi_id}`,
        data
    );
  }

  private informCancelUpload({ cmdi_id }: IUploadDataItem, msg?: string) {
    const data = msg ? { msg } : undefined;
    return this.requestHandler.post<unknown, { msg?: string }>(
        `${this.itemUploadEntityBase}/inform-cancel/${cmdi_id}`,
        data
    );
  }

  private async generateUploadItems(uploadData: IUploadDataNew) {
    const data = await this.requestHandler.post<IUploadData, IUploadDataNew>(
        `${this.uploadEntityBase}`,
        uploadData
    );

    if (!data.cargaDocumentosItems || data.cargaDocumentosItems.length === 0) {
      throw new Error('Hubo un error al intentar cargar el archivo');
    }

    return data;
  }

  private fileByUploadItem(files: FileToUpload[], item: IUploadDataItem): File {
    const fileReference = files.find((file) => file.isIdentifiedBy(item.cmdi_doc_uuid));
    if (!fileReference) throw new Error('Hubo un error a recuperar el archivo');
    return fileReference.getFile();
  }
}

export default DocumentsUploader;
